import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Navbar";
import { usePageTitle } from "@/components/Motion";
import SEO from "@/components/SEO";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  usePageTitle("CMS Login");
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-orange focus:ring-2 focus:ring-orange/20";

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4" data-testid="cms-login-page">
      <SEO title="CMS Login" noindex />
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-border bg-cream2 p-8 dark:bg-card sm:p-10">
          <Link to="/"><Wordmark /></Link>
          <h1 className="mt-8 font-display text-2xl font-bold tracking-tight">Team CMS</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to manage projects and journal posts.</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            {error && (
              <p className="rounded-lg border border-coral/40 bg-coral/10 px-4 py-3 text-sm text-coral" data-testid="cms-login-error">{error}</p>
            )}
            <div>
              <label htmlFor="al-email" className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Email</label>
              <input id="al-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="cms-login-email-input" placeholder="admin@incolourfilms.com" />
            </div>
            <div>
              <label htmlFor="al-password" className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Password</label>
              <input id="al-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="cms-login-password-input" placeholder="••••••••" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="cms-login-submit-btn"
              className="w-full rounded-full bg-ink px-8 py-3.5 text-sm font-semibold text-cream transition-colors hover:bg-orange disabled:opacity-60 dark:bg-cream dark:text-ink dark:hover:bg-orange dark:hover:text-cream"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
        <Link to="/" className="mt-6 block text-center text-sm text-muted-foreground hover:text-orange" data-testid="cms-login-back-link">← Back to site</Link>
      </div>
    </div>
  );
}
